import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./SearchBox.module.css";

type PagefindResult = {
  url: string;
  meta: { title?: string };
  excerpt: string;
};

type PagefindApi = {
  search: (query: string) => Promise<{
    results: { data: () => Promise<PagefindResult> }[];
  }>;
};

let pagefindPromise: Promise<PagefindApi | null> | null = null;

const PAGEFIND_ENTRY = "/pagefind/pagefind.js";

// eslint-disable-next-line no-new-func
const dynamicImport = new Function(
  "specifier",
  "return import(specifier)",
) as (specifier: string) => Promise<PagefindApi>;

function loadPagefind(): Promise<PagefindApi | null> {
  if (!pagefindPromise) {
    pagefindPromise = dynamicImport(PAGEFIND_ENTRY).catch((err) => {
      console.warn(
        '[kira-arts-docs] Search index not found at /pagefind/pagefind.js. Run "npm run build" (or "npm run search:build") before "npm run dev" to generate it.',
        err,
      );
      return null;
    });
  }
  return pagefindPromise;
}

function toAppPath(pagefindUrl: string): string {
  const path = pagefindUrl.replace(/index\.html$/, "").replace(/\/$/, "");
  return path === "" ? "/" : path;
}

export default function SearchBox() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [results, setResults] = useState<PagefindResult[]>([]);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      return;
    }

    let cancelled = false;
    const id = setTimeout(async () => {
      const pagefind = await loadPagefind();
      if (!pagefind) {
        if (!cancelled) setUnavailable(true);
        return;
      }
      const { results: raw } = await pagefind.search(trimmed);
      const data = await Promise.all(raw.slice(0, 8).map((r) => r.data()));
      if (!cancelled) setResults(data);
    }, 150);

    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [query]);

  function goTo(url: string) {
    navigate(toAppPath(url));
    setOpen(false);
    setQuery("");
  }

  return (
    <div className={styles.wrap} ref={boxRef}>
      <input
        type="search"
        className={styles.input}
        placeholder={t("search.placeholder")}
        value={query}
        onFocus={() => setOpen(true)}
        onChange={(e) => setQuery(e.target.value)}
      />

      {open && query.trim().length >= 2 && (
        <div className={styles.panel}>
          {unavailable && <p className={styles.hint}>{t("search.unavailable")}</p>}
          {!unavailable && results.length === 0 && (
            <p className={styles.hint}>{t("search.noResults")}</p>
          )}
          {results.map((r) => (
            <button
              key={r.url}
              type="button"
              className={styles.result}
              onClick={() => goTo(r.url)}
            >
              <span className={styles.resultTitle}>{r.meta.title ?? r.url}</span>
              <span
                className={styles.resultExcerpt}
                dangerouslySetInnerHTML={{ __html: r.excerpt }}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
